import { useEffect, useMemo, useState } from "react";
import { api, fmtMoney } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, PageBody } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Play, Plus, Trash2, Wand2, TrendingUp, TrendingDown } from "lucide-react";
import { toast } from "sonner";

const MONTHS = ["Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec", "Jan", "Feb", "Mar"];

function currentFy() {
    const t = new Date();
    const sy = t.getMonth() < 3 ? t.getFullYear() - 1 : t.getFullYear();
    return `${sy}-${String((sy + 1) % 100).padStart(2, "0")}`;
}

function fyOptions() {
    const t = new Date();
    const sy = t.getMonth() < 3 ? t.getFullYear() - 1 : t.getFullYear();
    return [-1, 0, 1].map((d) => `${sy + d}-${String((sy + d + 1) % 100).padStart(2, "0")}`);
}

export default function Budgets() {
    const { activeOrg } = useAuth();
    const [fy, setFy] = useState(currentFy());
    const [data, setData] = useState(null);
    const [accounts, setAccounts] = useState([]);
    const [budgets, setBudgets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editor, setEditor] = useState({ open: false, budget: null });
    const [seedOpen, setSeedOpen] = useState(false);

    const run = async () => {
        setLoading(true);
        try {
            const [{ data: report }, { data: bs }, { data: accts }] = await Promise.all([
                api.get("/reports/budget-vs-actuals", { params: { fy } }),
                api.get("/budgets", { params: { fy } }),
                api.get("/coa"),
            ]);
            setData(report);
            setBudgets(bs);
            setAccounts(accts.filter((a) => ["income", "cogs", "expense", "other_income", "other_expense"].includes(a.type)));
        } finally { setLoading(false); }
    };

    useEffect(() => { run(); /* eslint-disable-next-line */ }, [fy]);

    const sectionsMeta = [
        { type: "income", label: "Operating Income" },
        { type: "cogs", label: "Cost of Goods Sold" },
        { type: "expense", label: "Operating Expense" },
        { type: "other_income", label: "Non-Operating Income" },
        { type: "other_expense", label: "Non-Operating Expense" },
    ];

    return (
        <>
            <PageHeader
                kicker="REPORTS • PLANNING"
                title="Budget vs Actuals"
                subtitle="Set annual or monthly targets per account, then track variance against actuals as the year unfolds."
            />
            <PageBody>
                <div className="bg-card border border-border rounded-md p-4 mb-4 flex flex-wrap items-end gap-3" data-testid="budget-filters">
                    <div className="flex flex-col gap-1.5">
                        <Label className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-semibold">Financial year</Label>
                        <Select value={fy} onValueChange={setFy}>
                            <SelectTrigger className="h-9 w-[140px]" data-testid="budget-fy"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {fyOptions().map((f) => <SelectItem key={f} value={f}>FY {f}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="ml-auto flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => setSeedOpen(true)} data-testid="budget-seed-btn">
                            <Wand2 className="w-3.5 h-3.5 mr-1.5" />Seed from actuals
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setEditor({ open: true, budget: null })} data-testid="budget-add-btn">
                            <Plus className="w-3.5 h-3.5 mr-1.5" />Add budget line
                        </Button>
                        <Button size="sm" onClick={run} data-testid="budget-run">
                            <Play className="w-3.5 h-3.5 mr-1.5" />Refresh
                        </Button>
                    </div>
                </div>

                {data && (
                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-3 mb-4">
                        <Kpi label="Budgeted Net Profit" value={data.net_profit.budget} />
                        <Kpi label="Actual Net Profit" value={data.net_profit.actual} />
                        <Kpi
                            label="Variance"
                            value={data.net_profit.variance}
                            tone={data.net_profit.variance >= 0 ? "positive" : "destructive"}
                            icon={data.net_profit.variance >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                        />
                        <Kpi label="Achievement" value={data.net_profit.budget ? (data.net_profit.actual / data.net_profit.budget * 100) : 0} suffix="%" decimals={1} />
                    </div>
                )}

                {loading && <div className="text-center text-muted-foreground py-12">Loading…</div>}

                {data && !loading && (
                    <div className="bg-card border border-border rounded-md overflow-hidden" data-testid="budget-report">
                        <table className="w-full text-[13px]">
                            <thead className="bg-muted/40 border-b border-border">
                                <tr className="text-left text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-semibold">
                                    <th className="px-4 py-2.5 w-[36%]">Account</th>
                                    <th className="px-4 py-2.5 text-right">Budget</th>
                                    <th className="px-4 py-2.5 text-right">Actual</th>
                                    <th className="px-4 py-2.5 text-right">Variance</th>
                                    <th className="px-4 py-2.5 text-right">Var %</th>
                                    <th className="px-4 py-2.5 w-[60px]"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {sectionsMeta.map((sec) => {
                                    const sectionRows = data.rows.filter((r) => r.account_type === sec.type);
                                    if (sectionRows.length === 0) return null;
                                    const sectionTotal = data.sections[sec.type];
                                    return (
                                        <SectionBlock
                                            key={sec.type}
                                            sec={sec}
                                            rows={sectionRows}
                                            total={sectionTotal}
                                            onEdit={(row) => setEditor({ open: true, budget: budgets.find((b) => b.account_id === row.account_id) || { account_id: row.account_id, account_name: row.account_name, fy } })}
                                        />
                                    );
                                })}
                                <tr className="border-t-2 border-foreground bg-muted/30">
                                    <td className="px-4 py-3 font-semibold">Net Profit</td>
                                    <td className="px-4 py-3 text-right font-mono tabular-nums font-semibold">{fmtMoney(data.net_profit.budget)}</td>
                                    <td className="px-4 py-3 text-right font-mono tabular-nums font-semibold">{fmtMoney(data.net_profit.actual)}</td>
                                    <td className={`px-4 py-3 text-right font-mono tabular-nums font-semibold ${data.net_profit.variance >= 0 ? "text-positive" : "text-destructive"}`}>{fmtMoney(data.net_profit.variance)}</td>
                                    <td className="px-4 py-3 text-right font-mono tabular-nums">—</td>
                                    <td></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                )}
            </PageBody>

            <BudgetEditor
                open={editor.open}
                onOpenChange={(o) => setEditor((e) => ({ ...e, open: o }))}
                budget={editor.budget}
                accounts={accounts}
                fy={fy}
                onSaved={async () => { setEditor({ open: false, budget: null }); await run(); }}
            />

            <SeedFromActualsDialog
                open={seedOpen}
                onOpenChange={setSeedOpen}
                targetFy={fy}
                onSeeded={async () => { setSeedOpen(false); await run(); }}
            />
        </>
    );
}

function Kpi({ label, value, tone, icon, suffix = "", decimals = 0 }) {
    const formatted = suffix === "%" ? `${(value || 0).toFixed(decimals)}${suffix}` : fmtMoney(value || 0);
    return (
        <div className="bg-card border border-border rounded-md p-4">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-semibold">
                {icon}
                {label}
            </div>
            <div className={`mt-2 text-2xl font-display font-light tracking-tight ${tone === "positive" ? "text-positive" : tone === "destructive" ? "text-destructive" : ""}`} data-testid={`budget-kpi-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                {formatted}
            </div>
        </div>
    );
}

function SectionBlock({ sec, rows, total, onEdit }) {
    return (
        <>
            <tr className="bg-muted/30">
                <td colSpan={6} className="px-4 py-2 font-semibold text-[12px] uppercase tracking-[0.1em]">{sec.label}</td>
            </tr>
            {rows.map((r) => (
                <tr key={r.account_id} className="hover:bg-muted/30" data-testid={`budget-row-${r.account_id}`}>
                    <td className="px-4 py-2 pl-6">
                        <span className="font-mono text-[11px] text-muted-foreground mr-2 tabular-nums">{r.account_code}</span>
                        {r.account_name}
                    </td>
                    <td className="px-4 py-2 text-right font-mono tabular-nums">{fmtMoney(r.budget)}</td>
                    <td className="px-4 py-2 text-right font-mono tabular-nums">{fmtMoney(r.actual)}</td>
                    <td className={`px-4 py-2 text-right font-mono tabular-nums ${r.favorable ? "text-positive" : "text-destructive"}`}>
                        {fmtMoney(r.variance)}
                    </td>
                    <td className={`px-4 py-2 text-right font-mono tabular-nums ${r.favorable ? "text-positive" : "text-destructive"}`}>
                        {r.variance_pct == null ? "—" : `${r.variance_pct.toFixed(1)}%`}
                    </td>
                    <td className="px-2 py-2">
                        <Button variant="ghost" size="sm" className="h-7" onClick={() => onEdit(r)} data-testid={`budget-edit-${r.account_id}`}>Edit</Button>
                    </td>
                </tr>
            ))}
            <tr className="border-t border-border">
                <td className="px-4 py-2 font-medium">Total {sec.label}</td>
                <td className="px-4 py-2 text-right font-mono tabular-nums font-medium">{fmtMoney(total.budget)}</td>
                <td className="px-4 py-2 text-right font-mono tabular-nums font-medium">{fmtMoney(total.actual)}</td>
                <td className="px-4 py-2 text-right font-mono tabular-nums font-medium">{fmtMoney(total.variance)}</td>
                <td></td>
                <td></td>
            </tr>
        </>
    );
}

function BudgetEditor({ open, onOpenChange, budget, accounts, fy, onSaved }) {
    const [accountId, setAccountId] = useState("");
    const [monthly, setMonthly] = useState(Array(12).fill(0));
    const [annual, setAnnual] = useState(0);
    const [mode, setMode] = useState("annual");
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        if (budget?.id) {
            setAccountId(budget.account_id);
            setMonthly(budget.monthly || Array(12).fill(0));
            setAnnual(budget.annual || 0);
            setMode("monthly");
            setNotes(budget.notes || "");
        } else if (budget?.account_id) {
            setAccountId(budget.account_id);
            setMonthly(Array(12).fill(0));
            setAnnual(0);
            setMode("annual");
            setNotes("");
        } else {
            setAccountId("");
            setMonthly(Array(12).fill(0));
            setAnnual(0);
            setMode("annual");
            setNotes("");
        }
    }, [open, budget]);

    const computedMonthly = useMemo(() => {
        if (mode === "annual") return Array(12).fill(annual / 12);
        return monthly;
    }, [mode, monthly, annual]);

    const computedAnnual = useMemo(() => computedMonthly.reduce((s, x) => s + (Number(x) || 0), 0), [computedMonthly]);

    const save = async () => {
        if (!accountId) return toast.error("Pick an account");
        setSaving(true);
        try {
            await api.post("/budgets", { fy, account_id: accountId, monthly: computedMonthly.map((x) => Number(x) || 0), notes });
            toast.success("Budget saved");
            onSaved();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to save");
        } finally { setSaving(false); }
    };

    const remove = async () => {
        if (!budget?.id) return;
        if (!confirm("Delete this budget line?")) return;
        await api.delete(`/budgets/${budget.id}`);
        toast.success("Budget deleted");
        onSaved();
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent side="right" className="w-full sm:max-w-[640px] overflow-y-auto" data-testid="budget-editor">
                <SheetHeader>
                    <SheetTitle>{budget?.id ? "Edit budget" : "Add budget line"}</SheetTitle>
                    <SheetDescription>Set monthly or annual target for this account in FY {fy}.</SheetDescription>
                </SheetHeader>
                <div className="mt-4 space-y-5">
                    <div>
                        <Label className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-semibold">Account</Label>
                        <Select value={accountId} onValueChange={setAccountId} disabled={!!budget?.id}>
                            <SelectTrigger className="mt-1" data-testid="budget-edit-account"><SelectValue placeholder="Choose…" /></SelectTrigger>
                            <SelectContent>
                                {accounts.map((a) => (
                                    <SelectItem key={a.id} value={a.id}>
                                        <span className="font-mono text-[11px] text-muted-foreground mr-2">{a.code}</span>
                                        {a.name}
                                        <span className="ml-2 text-[10px] uppercase text-muted-foreground">· {a.type.replace("_", " ")}</span>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div>
                        <Label className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-semibold">Mode</Label>
                        <div className="flex gap-2 mt-1">
                            <Button type="button" size="sm" variant={mode === "annual" ? "default" : "outline"} onClick={() => setMode("annual")} data-testid="budget-mode-annual">Annual (split evenly)</Button>
                            <Button type="button" size="sm" variant={mode === "monthly" ? "default" : "outline"} onClick={() => setMode("monthly")} data-testid="budget-mode-monthly">Per month</Button>
                        </div>
                    </div>

                    {mode === "annual" ? (
                        <div>
                            <Label className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-semibold">Annual target (₹)</Label>
                            <Input type="number" value={annual} onChange={(e) => setAnnual(Number(e.target.value) || 0)} className="mt-1 font-mono tabular-nums" data-testid="budget-annual-input" />
                            <p className="text-[11px] text-muted-foreground mt-1">→ {fmtMoney((annual || 0) / 12)} per month</p>
                        </div>
                    ) : (
                        <div>
                            <Label className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-semibold">Monthly targets (₹)</Label>
                            <div className="grid grid-cols-3 gap-2 mt-1">
                                {MONTHS.map((m, i) => (
                                    <div key={m}>
                                        <Label className="text-[10px] text-muted-foreground">{m}</Label>
                                        <Input type="number" value={monthly[i] || 0}
                                            onChange={(e) => setMonthly((arr) => arr.map((x, j) => j === i ? (Number(e.target.value) || 0) : x))}
                                            className="h-8 font-mono tabular-nums text-[12px]" data-testid={`budget-month-${i}`} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="bg-muted/40 rounded p-3 text-[12px]">
                        <span className="text-muted-foreground">Total annual budget:</span>
                        <span className="ml-2 font-mono tabular-nums font-medium">{fmtMoney(computedAnnual)}</span>
                    </div>

                    <div>
                        <Label className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-semibold">Notes</Label>
                        <Input value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1" data-testid="budget-notes" />
                    </div>

                    <div className="flex justify-between pt-3 border-t border-border">
                        {budget?.id ? (
                            <Button variant="ghost" size="sm" onClick={remove} className="text-destructive" data-testid="budget-delete">
                                <Trash2 className="w-3.5 h-3.5 mr-1.5" />Delete
                            </Button>
                        ) : <span />}
                        <div className="flex gap-2">
                            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
                            <Button size="sm" onClick={save} disabled={saving} data-testid="budget-save">
                                {saving ? "Saving…" : "Save budget"}
                            </Button>
                        </div>
                    </div>
                </div>
            </SheetContent>
        </Sheet>
    );
}

function SeedFromActualsDialog({ open, onOpenChange, targetFy, onSeeded }) {
    const [sourceFy, setSourceFy] = useState("");
    const [bumpPct, setBumpPct] = useState(0);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (open) {
            const [a, b] = targetFy.split("-");
            setSourceFy(`${parseInt(a, 10) - 1}-${String((parseInt(b, 10) - 1 + 100) % 100).padStart(2, "0")}`);
        }
    }, [open, targetFy]);

    const seed = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/budgets/copy-from-actuals", {
                source_fy: sourceFy,
                target_fy: targetFy,
                bump_pct: Number(bumpPct) || 0,
            });
            toast.success(`Seeded ${data.created_or_updated} budget lines from FY ${sourceFy}`);
            onSeeded();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to seed");
        } finally { setBusy(false); }
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent side="right" className="w-full sm:max-w-[480px]" data-testid="budget-seed-dialog">
                <SheetHeader>
                    <SheetTitle>Seed budgets from actuals</SheetTitle>
                    <SheetDescription>Copy a previous year's actuals into FY {targetFy} as the starting budget. Optionally bump up by a percentage.</SheetDescription>
                </SheetHeader>
                <div className="mt-4 space-y-4">
                    <div>
                        <Label className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-semibold">Source FY (actuals to copy)</Label>
                        <Input value={sourceFy} onChange={(e) => setSourceFy(e.target.value)} placeholder="2024-25" className="mt-1" data-testid="budget-seed-source" />
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-semibold">Bump %</Label>
                        <Input type="number" value={bumpPct} onChange={(e) => setBumpPct(e.target.value)} placeholder="0" className="mt-1" data-testid="budget-seed-bump" />
                        <p className="text-[11px] text-muted-foreground mt-1">e.g. 10 = 10% higher than last year actuals.</p>
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                        <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Cancel</Button>
                        <Button size="sm" onClick={seed} disabled={busy} data-testid="budget-seed-confirm">
                            {busy ? "Seeding…" : `Seed FY ${targetFy}`}
                        </Button>
                    </div>
                </div>
            </SheetContent>
        </Sheet>
    );
}
